import React, { useContext, useEffect, useState, ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../config/constants';
import { Input, Button, Spin, Alert, Card, Form, Upload, message, Image, Row, Col } from 'antd';
import { UploadOutlined, SaveOutlined, ArrowLeftOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';

const { TextArea } = Input;

interface FitCritic {
  kritik: string;
  fitImageUrl?: string;
  fitImageUrls?: string[];
}

interface Product {
  _id: string;
  modelName: string;
  fitCritic?: FitCritic;
}

const FitKritikEditPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const canView = isAdmin || hasPermission('fitcrt', 'read');
  const canEdit = isAdmin || hasPermission('fitcrt', 'update') || hasPermission('fitcrt', 'create');

  useEffect(() => {
    if (productId && canView) { 
      fetchProductDetails();
    } else {
      if (!productId && canView) {
        setError("Ürün ID bulunamadı.");
        message.error("Ürün ID'si bulunamadı. Lütfen listeye geri dönün.");
      } else if (productId && !canView) {
        setError("Bu sayfayı görüntüleme yetkiniz yok.");
        message.error("Bu sayfayı görüntüleme yetkiniz yok.");
        navigate('/fit');
      } else if (!canEdit) {
        setError("Bu sayfada düzenleme yapma yetkiniz yok.");
        message.error("Bu sayfada düzenleme yapma yetkiniz yok.");
        navigate(`/fit/${productId}`);
      }
    }
  }, [productId, canView, canEdit, navigate]);

  const fetchProductDetails = async () => {
    if (!productId) {
      setError("Ürün ID bulunamadı.");
      message.error("Ürün ID'si eksik, veri getirilemiyor.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/products/${productId}`);
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Ürün bulunamadı.');
        } else {
          throw new Error('Ürün detayları getirilemedi');
        }
      }
      const data: Product = await res.json();
      setProduct(data);
      form.setFieldsValue({
        kritik: data.fitCritic?.kritik || '',
      });
      
      if (data.fitCritic?.fitImageUrls && data.fitCritic.fitImageUrls.length > 0) {
        const files = data.fitCritic.fitImageUrls.map((url, index) => ({
          uid: `-${index + 1}`,
          name: `fit_image_${index + 1}.png`,
          status: 'done' as const,
          url: url,
          thumbUrl: `${API_URL}${url}`,
        }));
        setFileList(files);
      } else if (data.fitCritic?.fitImageUrl) {
        setFileList([
          {
            uid: '-1',
            name: 'fit_image.png',
            status: 'done' as const,
            url: data.fitCritic.fitImageUrl,
            thumbUrl: `${API_URL}${data.fitCritic.fitImageUrl}`,
          },
        ]);
      } else {
        setFileList([]);
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
      message.error(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: { kritik: string }) => {
    if (!canEdit || !product) return;
    setSaving(true);
    setError(null);
    try {
      let uploadedImageUrls: string[] = [];
      
      if (product.fitCritic?.fitImageUrls) {
        uploadedImageUrls = product.fitCritic.fitImageUrls.filter(url => 
          fileList.some(file => file.url === url));
      } else if (product.fitCritic?.fitImageUrl && fileList.some(file => file.url === product.fitCritic?.fitImageUrl)) {
        uploadedImageUrls = [product.fitCritic.fitImageUrl];
      }

      const newImageFiles = fileList.filter(file => file.originFileObj);
      
      for (const file of newImageFiles) {
        if (file.originFileObj) {
          const formData = new FormData();
          formData.append('file', file.originFileObj as Blob);
          const uploadRes = await fetch(`${API_URL}/ftupload`, {
            method: 'POST',
            body: formData,
          });
          
          if (!uploadRes.ok) throw new Error('Görsel yüklenemedi');
          
          const uploadData = await uploadRes.json();
          uploadedImageUrls.push(uploadData.url);
        }
      }
      
      const payload = {
        kritik: values.kritik,
        fitImageUrls: uploadedImageUrls.length > 0 ? uploadedImageUrls : undefined,
        fitImageUrl: uploadedImageUrls.length > 0 ? uploadedImageUrls[0] : undefined
      };

      const res = await fetch(`${API_URL}/products/${product._id}/fit-critic`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Kaydetme başarısız, sunucu yanıtı okunamadı' }));
        throw new Error(errorData.message || 'Kaydetme başarısız');
      }

      message.success('Fit kritik başarıyla kaydedildi');
      navigate(`/fit/${product._id}`); 
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
      message.error(err.message || 'Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    if (newFileList.length > 10) {
      message.warning('En fazla 10 adet fotoğraf yükleyebilirsiniz.');
      setFileList(newFileList.slice(0, 10));
      return;
    }
    setFileList(newFileList);
  };

  // Resim önizleme işleyicisi
  const handlePreview = (file: UploadFile) => {
    if (file.url && !file.originFileObj) {
      // Mevcut bir görselse, API_URL ile birlikte göster
      const previewUrl = `${API_URL}${file.url}`;
      const previewWindow = window.open('');
      if (previewWindow) {
        previewWindow.document.write(`
          <html>
            <head>
              <title>Görsel Önizleme</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; background: #f0f0f0; }
                img { max-width: 90%; max-height: 90vh; box-shadow: 0 0 20px rgba(0,0,0,0.15); }
              </style>
            </head>
            <body>
              <img src="${previewUrl}" alt="Fit Fotoğrafı" />
            </body>
          </html>
        `);
      }
    }
  };

  // Özel upload item render fonksiyonu - resimlerin önizlemesini API_URL ile göster
  const customItemRender = (originNode: React.ReactElement, file: UploadFile, fileList: UploadFile[]) => {
    // Eğer dosya yüklenmişse ve url varsa, önizlemede API_URL ile göster
    if (file.status === 'done' && file.url && !file.originFileObj) {
      const previewProps = {
        ...originNode.props,
        onClick: () => {
          // Resim tıklandığında büyük önizleme
          const src = `${API_URL}${file.url}`;
          const imgWindow = window.open('');
          if (imgWindow) {
            imgWindow.document.write(`<img src="${src}" style="max-width: 100%; max-height: 100vh;" />`);
          }
        }
      };
      
      // Resim önizleme kısmını özelleştir
      return React.cloneElement(originNode, previewProps);
    }
    return originNode;
  };

  if (!canEdit && !loading && product) {
    return (
      <div className="p-8">
        <Alert message="Yetkisiz Erişim" description="Bu sayfada düzenleme yapma yetkiniz yok. Görüntüleme sayfasına yönlendiriliyorsunuz..." type="error" showIcon />
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="text-center py-8">
        <Spin size="large" tip="Veriler yükleniyor..." />
      </div>
    );
  }

  if (error && !product && !loading) {
    return <Alert message="Veri Yükleme Hatası" description={error} type="error" showIcon className="m-8" />;
  }

  if (!product && !loading) {
    return <Alert message="Ürün Bulunamadı" description="Belirtilen ürün yüklenemedi veya bulunamadı." type="warning" showIcon className="m-8" />;
  }
  
  if (!product) {
      return <Alert message="Hata" description="Ürün bilgileri yüklenemedi. Lütfen sayfayı yenileyin veya daha sonra tekrar deneyin." type="error" showIcon className="m-8" />;
  }

  const pageTitle = product
    ? `${product.modelName} - ${product.fitCritic && product.fitCritic.kritik ? 'Fit Kritik Düzenle' : 'Fit Kritiği Ekle'}`
    : 'Fit Kritik İşlemi';

  return (
    <div className="p-4 md:p-8">
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(product ? `/fit/${product._id}` : '/fit')} 
        className="mb-4"
      >
        {product ? 'Görüntüleme Sayfasına Dön' : 'Fit Kritik Listesine Dön'}
      </Button>
      <Card title={pageTitle} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {error && saving && <Alert message="Kaydetme Hatası" description={error} type="error" showIcon className="mb-4" />}
        <Form form={form} layout="vertical" onFinish={onFinish} disabled={!canEdit || saving}>
          <Form.Item
            label="Fit Kritik Notları"
            name="kritik"
            rules={[{ required: true, message: 'Lütfen fit kritik notlarını girin!' }]}
          >
            <TextArea rows={4} placeholder="Fit ile ilgili kritiklerinizi buraya yazın..." />
          </Form.Item>

          <Form.Item label="Fit Fotoğrafları">
            <Upload
              action={`${API_URL}/ftupload`}
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={() => false}
              maxCount={10}
              multiple
              disabled={!canEdit || saving}
              itemRender={customItemRender}
              onPreview={handlePreview}
            >
              {fileList.length >= 10 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Fotoğraf Yükle</div>
                </div>
              )}
            </Upload>
            <div className="text-gray-500 text-sm mt-1">
              En fazla 10 adet fotoğraf yükleyebilirsiniz.
            </div>
          </Form.Item>

          {canEdit && (
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                {product?.fitCritic && product.fitCritic.kritik ? 'Değişiklikleri Kaydet' : 'Fit Kritiğini Kaydet'}
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default FitKritikEditPage; 